import React, { useEffect, useRef, useState } from 'react';
import './resources.styles.scss';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import SearchBar from '@/components/SHARED/search-bar/search-bar.component';
import UnauthLayout from '@/components/layout-unauth/unauth-layout.component';
import { getCollectionFromFirestore } from '@/utils/getDataFromDbSimplified';
import { useSelector } from 'react-redux';
import { properCase } from '@/utils/properCase';
import Modal from '@/components/modal/modal.component';
import { UseCloudAdminFn } from '@/utils/firebase.utils';

const PAGE_SIZE = 15;

const ResourcesPage = () => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const [resources, setResources] = useState([]);
  const resourcesRef = useRef({});
  const [existingTags, setExistingTags] = useState([]);
  const [existingTypes, setExistingTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTag, setActiveTag] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [showAllTags, setShowAllTags] = useState(false);
  let resourcesListener;

  useEffect(() => {
    getResources();
    return () => {
      if (resourcesListener) resourcesListener();
    }
  }, [])

  function getResources() {
    getCollectionFromFirestore({
      'path': 'resources',
      'callback': callback,
      'handleListener': handleListener
    })
    function callback(data) {
      resourcesRef.current = {
        ...resourcesRef.current,
        ...data
      }
      const all = Object.values(resourcesRef.current)
      .filter(r => r.status === 'active')
      .sort((a,b) => b.visitCount - a.visitCount);
      setResources(all);
      const tagCount = {};
      const typesSet = new Set();
      all.forEach(r => {
        r.tags?.forEach(t => { tagCount[t] = (tagCount[t] || 0) + 1; });
        if (r.type) typesSet.add(r.type);
      });
      const sortedTags = Object.entries(tagCount)
        .sort((a, b) => b[1] - a[1])
        .map(([tag]) => tag);
      setExistingTags(sortedTags);
      setExistingTypes(Array.from(typesSet).sort());
    }
    function handleListener(listener) {
      resourcesListener = listener;
    }
  }

  function handleSearch(val) {
    setSearchTerm(val);
    setVisibleCount(PAGE_SIZE);
  }

  function handleTagClick(tag) {
    setActiveTag(prev => prev === tag ? null : tag);
    setVisibleCount(PAGE_SIZE);
  }

  function handleTypeClick(type) {
    setActiveType(prev => prev === type ? null : type);
    setVisibleCount(PAGE_SIZE);
  }

  async function visitResource(resource) {
    if (resource.url) {
      window.open(resource.url, '_blank');
      const res = await UseCloudAdminFn(
        'iterateResourceVisitCounts',
        { resourceId: resource.id }
      )
      console.log(res);
      // Optionally, you could also track clicks here by updating the resource in Firestore
    }
  }

  const filtered = resources.filter(r => {
    const matchesTag = !activeTag || r.tags?.includes(activeTag);
    const matchesType = !activeType || r.type === activeType;
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      r.title?.toLowerCase().includes(term) ||
      r.description?.toLowerCase().includes(term) ||
      r.tags?.some(t => t.toLowerCase().includes(term));
    return matchesTag && matchesType && matchesSearch;
  });

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  return (
    <UnauthLayout>
      <div className={`resources-page${screenSize === 'mobile' ? ' mobile' : ''}`}>
        <div className='resources-card g-card'>
          <div className='section-title'>Resource Center</div>
          <hr />
          <div className='resources-content'>

            <SearchBar search={handleSearch} idLabel='resources-search' />

            {existingTypes.length > 0 && (
              <div className='tag-filter-row'>
                {existingTypes.map(type => (
                  <button
                    key={type}
                    className={`tag-filter-btn${activeType === type ? ' active' : ''}`}
                    onClick={() => handleTypeClick(type)}
                  >
                    {properCase(type)}
                  </button>
                ))}
              </div>
            )}

            {existingTags.length > 0 && (
              <div className='tag-filter-row'>
                {existingTags.slice(0, 5).map(tag => (
                  <button
                    key={tag}
                    className={`tag-filter-btn${activeTag === tag ? ' active' : ''}`}
                    onClick={() => handleTagClick(tag)}
                  >
                    {tag}
                  </button>
                ))}
                {existingTags.length > 5 && (
                  <button
                    className='tag-filter-btn'
                    onClick={() => setShowAllTags(true)}
                  >
                    +{existingTags.length - 5} more...
                  </button>
                )}
              </div>
            )}

            <Modal
              show={showAllTags}
              cancel={() => setShowAllTags(false)}
              text='Filter by Tag'
              closeButton={true}
            >
              <div className='tag-filter-row modal-tags'>
                {[...existingTags].sort().map(tag => (
                  <button
                    key={tag}
                    className={`tag-filter-btn${activeTag === tag ? ' active' : ''}`}
                    onClick={() => { handleTagClick(tag); setShowAllTags(false); }}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </Modal>

            {filtered.length === 0 ? (
              <p className='meta no-results'>No resources match your search.</p>
            ) : (
              <>
                <div className='resources-list'>
                  {visible.map(resource => (
                    <div key={resource.id} className={`g-list-item resource-item${resource.status === 'hidden' ? ' hidden-resource' : ''}`}>
                      <div className='resource-icon'>
                        <ImageContainer
                          src={`/images/icons/${resource.type}.png`}
                          alt={resource.type}
                          containerWidth='48px'
                          containerHeight='48px'
                        />
                      </div>
                      <div className='resource-body'>
                        <div className='resource-header'>
                          <span className='resource-title'>{resource.title}</span>
                          <span className='resource-type-badge'>{resource.type}</span>
                        </div>
                        {resource.description && (
                          <p className='resource-description'>{resource.description}</p>
                        )}
                        {resource.tags?.length > 0 && (
                          <div className='resource-tags'>
                            {resource.tags.map(tag => (
                              <button
                                key={tag}
                                className={`tag-filter-btn small${activeTag === tag ? ' active' : ''}`}
                                onClick={() => handleTagClick(tag)}
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        )}
                        {resource.url && (
                        <>
                        <div className="g-space-0-5"></div>
                        <div className='buttons'>
                          <div className='meta'>{resource.visitCount || 0} visits</div>
                          <button 
                            className='g-button med-btn'
                            onClick={() => visitResource(resource)}>
                            Visit Resource
                          </button>
                        </div>
                        </>
                      )}
                      </div>
                      
                    </div>
                  ))}
                </div>

                {hasMore && (
                  <div className='load-more-row'>
                    <button
                      className='g-button med-btn'
                      onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                    >
                      Load More ({filtered.length - visibleCount} remaining)
                    </button>
                  </div>
                )}
              </>
            )}

          </div>
        </div>
      </div>
    </UnauthLayout>
  )
}

export default ResourcesPage
